import { z } from 'zod';
import { daysBetween } from './calendar/dates.js';
import { holidayRegionSchema } from './calendar/holidays.js';
import { weekdays } from './capacity/capacity.js';
import { colorSchema, isoDateSchema } from './projects.js';
import { queryList } from './task-list.js';

/** RES-01, RES-02, ABS (manual fallback): master data of the resource planning. */

// --- Job roles (RES-01) ------------------------------------------------------------------------

export const jobRoleNameSchema = z.string().trim().min(1).max(100);
/** Hex colour `#rrggbb` for chips and heatmap groups. */
export const jobRoleColorSchema = colorSchema;

/** Reference to an area (RES-15). */
export const areaRefSchema = z.object({ id: z.uuid(), name: z.string() });
export type AreaRef = z.infer<typeof areaRefSchema>;

export const jobRoleSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  color: z.string(),
  active: z.boolean(),
  /** At most one area (RES-15); null = without area. */
  area: areaRefSchema.nullable(),
  /** Number of people with this role. */
  userCount: z.number().int(),
});
export type JobRole = z.infer<typeof jobRoleSchema>;

export const jobRoleListResponseSchema = z.object({ items: z.array(jobRoleSchema) });
export type JobRoleListResponse = z.infer<typeof jobRoleListResponseSchema>;

export const createJobRoleBodySchema = z.object({
  name: jobRoleNameSchema,
  color: jobRoleColorSchema,
  /** RES-15; omitted or null = without area. */
  areaId: z.uuid().nullable().optional(),
});
export type CreateJobRoleBody = z.infer<typeof createJobRoleBodySchema>;

export const updateJobRoleBodySchema = z.object({
  name: jobRoleNameSchema.optional(),
  color: jobRoleColorSchema.optional(),
  active: z.boolean().optional(),
  areaId: z.uuid().nullable().optional(),
});
export type UpdateJobRoleBody = z.infer<typeof updateJobRoleBodySchema>;

/** PUT /admin/resources/users/:id/job-roles – replaces the set; exactly one primary role if any. */
export const setUserJobRolesBodySchema = z
  .object({
    jobRoleIds: z.array(z.uuid()).max(20),
    primaryJobRoleId: z.uuid().nullable(),
  })
  .refine((body) => new Set(body.jobRoleIds).size === body.jobRoleIds.length, {
    message: 'Doppelte Rolle',
    path: ['jobRoleIds'],
  })
  .refine(
    (body) =>
      body.jobRoleIds.length === 0
        ? body.primaryJobRoleId === null
        : body.primaryJobRoleId !== null && body.jobRoleIds.includes(body.primaryJobRoleId),
    { message: 'Genau eine der Rollen muss primär sein', path: ['primaryJobRoleId'] },
  );
export type SetUserJobRolesBody = z.infer<typeof setUserJobRolesBodySchema>;

// --- Areas (RES-15) and skills (RES-16) --------------------------------------------------------

export const areaNameSchema = z.string().trim().min(1).max(100);

export const areaSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  active: z.boolean(),
  /** Number of job roles in this area. */
  jobRoleCount: z.number().int(),
});
export type Area = z.infer<typeof areaSchema>;

export const areaListResponseSchema = z.object({ items: z.array(areaSchema) });
export type AreaListResponse = z.infer<typeof areaListResponseSchema>;

export const createAreaBodySchema = z.object({ name: areaNameSchema });
export type CreateAreaBody = z.infer<typeof createAreaBodySchema>;

export const updateAreaBodySchema = z.object({
  name: areaNameSchema.optional(),
  active: z.boolean().optional(),
});
export type UpdateAreaBody = z.infer<typeof updateAreaBodySchema>;

export const skillNameSchema = z.string().trim().min(1).max(100);

export const skillSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  active: z.boolean(),
  /** Number of people with this skill. */
  userCount: z.number().int(),
});
export type Skill = z.infer<typeof skillSchema>;

export const skillListResponseSchema = z.object({ items: z.array(skillSchema) });
export type SkillListResponse = z.infer<typeof skillListResponseSchema>;

export const createSkillBodySchema = z.object({ name: skillNameSchema });
export type CreateSkillBody = z.infer<typeof createSkillBodySchema>;

export const updateSkillBodySchema = z.object({
  name: skillNameSchema.optional(),
  active: z.boolean().optional(),
});
export type UpdateSkillBody = z.infer<typeof updateSkillBodySchema>;

/** Most skills one person can have. */
export const MAX_SKILLS_PER_USER = 50;

/** PUT /admin/resources/users/:id/skills – replaces the set (job_roles.manage only, E-38). */
export const setUserSkillsBodySchema = z.object({
  skillIds: z
    .array(z.uuid())
    .max(MAX_SKILLS_PER_USER)
    .refine((ids) => new Set(ids).size === ids.length, { message: 'Doppelter Skill' }),
});
export type SetUserSkillsBody = z.infer<typeof setUserSkillsBodySchema>;

/** GET /options/areas and /options/skills – active entries for internal users. */
export const catalogOptionsResponseSchema = z.object({ items: z.array(areaRefSchema) });
export type CatalogOptionsResponse = z.infer<typeof catalogOptionsResponseSchema>;

/**
 * GET /people/skills – "Wer kann was?" (RES-16) for every internal user: who has which skills,
 * job roles and areas. Never utilization, bookings, absences or e-mail addresses.
 */
export const peopleSkillsQuerySchema = z.object({
  /** People having all of these skills. */
  skillIds: queryList(z.uuid()).optional(),
  /** People with any job role in this area. */
  areaId: z.uuid().optional(),
  /** People holding this job role (any, not only primary). */
  jobRoleId: z.uuid().optional(),
});
export type PeopleSkillsQuery = z.infer<typeof peopleSkillsQuerySchema>;

export const personSkillsSchema = z.object({
  user: z.object({ id: z.uuid(), name: z.string(), avatarUrl: z.string().nullable() }),
  primaryJobRole: z.object({ id: z.uuid(), name: z.string(), color: z.string() }).nullable(),
  /** All job roles, primary first. */
  jobRoles: z.array(z.object({ id: z.uuid(), name: z.string() })),
  /** Areas of the job roles, by name. */
  areas: z.array(areaRefSchema),
  skills: z.array(areaRefSchema),
});
export type PersonSkills = z.infer<typeof personSkillsSchema>;

export const peopleSkillsResponseSchema = z.object({ items: z.array(personSkillsSchema) });
export type PeopleSkillsResponse = z.infer<typeof peopleSkillsResponseSchema>;

// --- Work profiles (RES-02) --------------------------------------------------------------------

/** Hours per day: 0–24 in steps of a quarter hour (stored as numeric(4,2)). */
export const dailyHoursSchema = z
  .number()
  .min(0)
  .max(24)
  .refine((hours) => Number.isInteger(hours * 4), { message: 'Nur Viertelstunden' });

export const weeklyHoursSchema = z.object(
  Object.fromEntries(weekdays.map((day) => [day, dailyHoursSchema])) as Record<
    (typeof weekdays)[number],
    typeof dailyHoursSchema
  >, // fromEntries loses the key type; the keys are exactly `weekdays`
);

export const workProfileSchema = z.object({
  id: z.uuid(),
  validFrom: isoDateSchema,
  hours: weeklyHoursSchema,
});
export type WorkProfile = z.infer<typeof workProfileSchema>;

export const createWorkProfileBodySchema = z.object({
  validFrom: isoDateSchema,
  hours: weeklyHoursSchema,
});
export type CreateWorkProfileBody = z.infer<typeof createWorkProfileBodySchema>;

export const updateWorkProfileBodySchema = createWorkProfileBodySchema.partial();
export type UpdateWorkProfileBody = z.infer<typeof updateWorkProfileBodySchema>;

// --- Resource data of a person (admin user detail) ---------------------------------------------

export const userJobRoleSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  color: z.string(),
  active: z.boolean(),
  isPrimary: z.boolean(),
});
export type UserJobRole = z.infer<typeof userJobRoleSchema>;

/** GET /admin/resources/users – internal, not deactivated users (about 100, no pagination). */
export const resourceUserSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  email: z.string(),
  holidayRegion: holidayRegionSchema.nullable(),
  /** Own bookable share (RES-13); null = company default. */
  bookablePercent: z.number().int().nullable(),
  primaryJobRole: z.object({ id: z.uuid(), name: z.string(), color: z.string() }).nullable(),
  jobRoleCount: z.number().int(),
  /** Number of stored work profiles; 0 = default profile. */
  workProfileCount: z.number().int(),
});
export type ResourceUser = z.infer<typeof resourceUserSchema>;

export const resourceUserListResponseSchema = z.object({ items: z.array(resourceUserSchema) });
export type ResourceUserListResponse = z.infer<typeof resourceUserListResponseSchema>;

/** GET /admin/resources/users/:id – internal users only. */
export const userResourcesSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  email: z.string(),
  /** Own region; null = company default. */
  holidayRegion: holidayRegionSchema.nullable(),
  effectiveHolidayRegion: holidayRegionSchema,
  /** Own bookable share of the working time (RES-13); null = company default. */
  bookablePercent: z.number().int().nullable(),
  effectiveBookablePercent: z.number().int(),
  jobRoles: z.array(userJobRoleSchema),
  /** Skills by name (RES-16); inactive ones stay until removed. */
  skills: z.array(z.object({ id: z.uuid(), name: z.string(), active: z.boolean() })),
  /** Sorted by `validFrom`; empty = default profile 8/8/8/8/8/0/0. */
  workProfiles: z.array(workProfileSchema),
});
export type UserResources = z.infer<typeof userResourcesSchema>;

/** Part of the working time that can be booked in projects, in whole percent (RES-13). */
export const bookablePercentSchema = z.number().int().min(0).max(100);

/** Only the given fields change. */
export const updateUserResourcesBodySchema = z.object({
  holidayRegion: holidayRegionSchema.nullable().optional(),
  bookablePercent: bookablePercentSchema.nullable().optional(),
});
export type UpdateUserResourcesBody = z.infer<typeof updateUserResourcesBodySchema>;

// --- Company settings (E-11) -------------------------------------------------------------------

export const resourceSettingsSchema = z.object({
  defaultHolidayRegion: holidayRegionSchema,
  /** Bookable share for everyone without an own value (RES-13). */
  defaultBookablePercent: bookablePercentSchema,
});
export type ResourceSettings = z.infer<typeof resourceSettingsSchema>;

/** Only the given settings change. */
export const updateResourceSettingsBodySchema = resourceSettingsSchema.partial();
export type UpdateResourceSettingsBody = z.infer<typeof updateResourceSettingsBodySchema>;

// --- Absences (manual fallback, ABS-01) ---------------------------------------------------------

export const halfDaySchema = z.enum(['none', 'first_half', 'second_half']);

/** Longest manual absence; longer periods are split (sabbatical, parental leave). */
export const MAX_ABSENCE_DAYS = 366;

/** The reason is never stored for manual absences (E-15), only "absent". */
export const absenceSchema = z.object({
  id: z.uuid(),
  startDate: isoDateSchema,
  endDate: isoDateSchema,
  halfDay: halfDaySchema,
  /** `manual` or the source system (Phase 8). */
  source: z.string(),
  /** Only manual absences can be changed here; imported ones belong to the source system. */
  editable: z.boolean(),
});
export type Absence = z.infer<typeof absenceSchema>;

export const absenceListResponseSchema = z.object({ items: z.array(absenceSchema) });
export type AbsenceListResponse = z.infer<typeof absenceListResponseSchema>;

/** Absences overlapping the range; without a range, from 90 days ago onwards. */
export const absenceListQuerySchema = z
  .object({ from: isoDateSchema.optional(), to: isoDateSchema.optional() })
  .refine((q) => !q.from || !q.to || q.from <= q.to, { message: 'Ungültiger Zeitraum' });
export type AbsenceListQuery = z.infer<typeof absenceListQuerySchema>;

type AbsencePeriod = { startDate: string; endDate: string; halfDay: z.infer<typeof halfDaySchema> };

function checkAbsencePeriod(value: AbsencePeriod, ctx: z.RefinementCtx) {
  if (value.endDate < value.startDate) {
    ctx.addIssue({ code: 'custom', message: 'Ende vor Beginn', path: ['endDate'] });
  } else if (daysBetween(value.startDate, value.endDate) >= MAX_ABSENCE_DAYS) {
    ctx.addIssue({ code: 'custom', message: 'Zeitraum zu lang', path: ['endDate'] });
  }
  if (value.halfDay !== 'none' && value.startDate !== value.endDate) {
    ctx.addIssue({ code: 'custom', message: 'Halbe Tage nur eintägig', path: ['halfDay'] });
  }
}

export const absenceBodySchema = z
  .object({
    startDate: isoDateSchema,
    endDate: isoDateSchema,
    halfDay: halfDaySchema.default('none'),
  })
  .superRefine(checkAbsencePeriod);
export type AbsenceBody = z.infer<typeof absenceBodySchema>;

/** PATCH replaces the whole period, so the checks above always see consistent values. */
export const updateAbsenceBodySchema = absenceBodySchema;

export const resourceProblemTypes = {
  jobRoleInUse: 'urn:pm-tool:problem:job-role-in-use',
  jobRoleInactive: 'urn:pm-tool:problem:job-role-inactive',
  areaInUse: 'urn:pm-tool:problem:area-in-use',
  areaInactive: 'urn:pm-tool:problem:area-inactive',
  skillInUse: 'urn:pm-tool:problem:skill-in-use',
  skillInactive: 'urn:pm-tool:problem:skill-inactive',
  workProfileExists: 'urn:pm-tool:problem:work-profile-exists',
  absenceOverlap: 'urn:pm-tool:problem:absence-overlap',
  absenceNotEditable: 'urn:pm-tool:problem:absence-not-editable',
  /** Bookings of archived projects cannot change (phase 7b). */
  projectArchived: 'urn:pm-tool:problem:project-archived',
  /** Person not internal/active, job role inactive or task of another project (phase 7b). */
  invalidAllocationTarget: 'urn:pm-tool:problem:invalid-allocation-target',
} as const;
